/**
 * 어드민 새 기능 안내(시안 B+C) 순수 헬퍼 — 표 admin_feature_notice · admin_feature_ack 를 읽은 뒤의 판정.
 * 데이터 모양은 RPC admin_feature_notice_overview() 의 notices[] 원소:
 *   { key, title, summary, steps:[{label,text}], link_path, link_label, screen_paths:[], tips:[{anchor,text}],
 *     published_at, expires_at, acked_by:[email], tip_acked_by:[email] }
 * 시안: docs/design/admin-feature-notice-variants.html · 문서: docs/pages/admin/AdminWhatsNew.md
 */

function list(v) {
  return Array.isArray(v) ? v : [];
}

/** 게시 중(만료 전)인가 */
export function isNoticeActive(notice, now = new Date()) {
  if (!notice) return false;
  const pub = notice.published_at ? new Date(notice.published_at).getTime() : 0;
  if (Number.isFinite(pub) && pub > now.getTime()) return false;
  if (!notice.expires_at) return true;
  const exp = new Date(notice.expires_at).getTime();
  return !Number.isFinite(exp) || exp > now.getTime();
}

/** 내가 「확인했어요」를 눌렀나 (띠·원장) */
export function isAckedBy(notice, email) {
  return !!email && list(notice?.acked_by).includes(email);
}

/** 내가 말풍선을 닫았나 */
export function isTipAckedBy(notice, email) {
  return !!email && list(notice?.tip_acked_by).includes(email);
}

/** 내가 아직 확인하지 않은 게시 중 안내 — 사이드바 배지 개수·대시보드 한 줄 */
export function unackedNotices(notices, email, now = new Date()) {
  return list(notices).filter((n) => isNoticeActive(n, now) && !isAckedBy(n, email));
}

/** 이 화면(경로)에 붙일 띠 — 아직 확인 안 한 것 중 가장 최근 하나. 없으면 null */
export function stripNoticeFor(path, notices, email, now = new Date()) {
  if (!path) return null;
  const hits = unackedNotices(notices, email, now).filter((n) =>
    list(n.screen_paths).some((p) => path === p || path.startsWith(`${p}/`))
  );
  return hits[0] || null;
}

/** 이 화면·이 자리에 띄울 말풍선 — 아직 닫지 않은 안내의 tips 중 anchor 가 맞는 것 (순서 보존) */
export function tipsFor(noticeKey, notices, email, now = new Date()) {
  const n = list(notices).find((x) => x.key === noticeKey);
  if (!n || !isNoticeActive(n, now) || isTipAckedBy(n, email)) return [];
  return list(n.tips).filter((t) => t && t.anchor && t.text);
}

/**
 * 지금 띄울 말풍선 한 개 — 아직 넘기지 않은 것 중, **지금 화면에 자리가 있는** 첫 번째(순서 보존).
 * 2026-09-27: 예전 순번 방식은 앞 말풍선의 자리가 이 화면에 없으면(다른 탭) 뒤 말풍선이 영영 안 떴다.
 * @param {Array<{anchor:string}>} tips  tipsFor 결과
 * @param {string[]} seen     이번 세션에 「다음」으로 넘긴 anchor
 * @param {string[]} mounted  지금 화면에 찍혀 있는 anchor
 * @returns {null | { anchor, index, total, last }}  last = 이것 말고 안 본 말풍선이 없음 → 「닫기」(서버에 기록)
 */
export function pickVisibleTip(tips, seen = [], mounted = []) {
  const all = list(tips);
  const unseen = all.map((t, index) => ({ anchor: t.anchor, index })).filter((t) => !list(seen).includes(t.anchor));
  const current = unseen.find((t) => list(mounted).includes(t.anchor));
  if (!current) return null;
  return { anchor: current.anchor, index: current.index, total: all.length, last: unseen.length === 1 };
}

/** 「운영팀 N/M 확인」 — 확인한 사람 수·총원·이메일 목록 */
export function ackSummary(notice, adminTotal) {
  const who = list(notice?.acked_by);
  const total = Math.max(Number(adminTotal) || 0, who.length);
  return { count: who.length, total, who, done: total > 0 && who.length >= total };
}

/** 원장 표시용 날짜 "2026-09-14" */
export function noticeDateLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}

// ── 말풍선 진행도 저장 (2026-10-15) ───────────────────────────────────────────────────────────
// 예전에는 「다음」으로 넘긴 자리가 이번 세션 메모리에만 남고, 마지막 말풍선의 「닫기」만 서버(kind='tip')에 기록됐다.
// 그래서 뒤 말풍선의 자리가 화면에 끝내 안 그려지면(코드에서 사라진 자리·다른 창) 「닫기」에 이르지 못하고,
// 새로고침·재진입마다 첫 말풍선부터 다시 떴다(2026-10-15 대표 실측: 「작품 배정」 탭 1/2 가 매번).
// 이제 「다음」도 ① 기기 로컬(계정별) ② 서버(kind='tip:<anchor>', 옛 DB 는 거부해도 무방) 에 남기고,
// 서버 응답의 `my_tip_anchors` 와 로컬을 합쳐 시작한다. 전부 넘기면 「닫기」와 같이 kind='tip' 으로 닫는다.

const TIP_PROGRESS_KEY = "laeebly.admin.tip.progress.v1";

function safeStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function uniq(arr) {
  return Array.from(new Set(list(arr).filter((v) => typeof v === "string" && v)));
}

/** 두 진행도 합치기 — { noticeKey: anchor[] }. 한쪽이라도 '*'(닫음)면 '*' */
export function mergeTipSeen(a, b) {
  const out = {};
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  keys.forEach((k) => {
    const merged = uniq([...list(a?.[k]), ...list(b?.[k])]);
    out[k] = merged.includes("*") ? ["*"] : merged;
  });
  return out;
}

/** 기기 로컬에 남긴 내 진행도 — 계정별. 없거나 깨졌으면 {} */
export function readTipProgress(email, storage = safeStorage()) {
  if (!email || !storage) return {};
  try {
    const raw = storage.getItem(`${TIP_PROGRESS_KEY}:${email}`);
    const v = raw ? JSON.parse(raw) : null;
    if (!v || typeof v !== "object" || Array.isArray(v)) return {};
    return mergeTipSeen({}, v);
  } catch {
    return {};
  }
}

export function writeTipProgress(email, progress, storage = safeStorage()) {
  if (!email || !storage) return;
  try {
    storage.setItem(`${TIP_PROGRESS_KEY}:${email}`, JSON.stringify(progress || {}));
  } catch {
    /* 저장 불가는 무시 — 서버 기록이 받쳐 준다 */
  }
}

/** 서버가 돌려준 「내가 넘긴 자리」(`my_tip_anchors`, 옛 RPC 는 없음) + 닫음(`tip_acked_by`) 을 진행도 모양으로 */
export function serverTipSeen(notices, email) {
  const out = {};
  list(notices).forEach((n) => {
    if (!n || !n.key) return;
    if (isTipAckedBy(n, email)) { out[n.key] = ["*"]; return; }
    const mine = uniq(n.my_tip_anchors);
    if (mine.length) out[n.key] = mine;
  });
  return out;
}

/** 이 안내의 말풍선을 전부 넘겼나 — 「닫기」와 같은 상태 */
export function allTipsSeen(notice, seen) {
  const s = list(seen);
  if (s.includes("*")) return true;
  const anchors = list(notice?.tips).map((t) => t && t.anchor).filter(Boolean);
  return anchors.length > 0 && anchors.every((a) => s.includes(a));
}
